import { useEffect, useRef, useState, useCallback } from "react";
import { Product, ProductType } from "../types/product";
import { RecipeType } from "../types/recipes";
import { ArticleType } from "../types/article";
import { isInViewport } from "@/helpers/global";
import { analyticsViewEvent } from "@/services/algolia/analytics";
import { algoliaConfig } from "@/services/algolia/config";
import { useAppSelector } from "./reactUseCustomHooks";
import { GlobalSearchSectionsType } from "../types/algolia";

export const useAlgoliaViewAnalyticsEvent = (
  items:
    | Array<Product>
    | Array<ProductType>
    | Array<RecipeType>
    | Array<ArticleType>,
  kind: GlobalSearchSectionsType
) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const setBounds = useState<DOMRect | null>(null)[1];
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const batchTimeout = useRef<NodeJS.Timeout | null>(null);
  const { uid: Uid } = useAppSelector((state) => state.gigyaslice);
  const userId = Uid ?? undefined;
  const [isMounted, setIsMounted] = useState(false);

  // Using Set to track both pending and sent items
  const pendingItems = useRef<Set<string>>(new Set());
  const sentItems = useRef<Set<string>>(new Set());

  const sendBatchAnalytics = useCallback(() => {
    if (pendingItems.current.size === 0) return;

    const objectIDs = Array.from(pendingItems.current);
    const index = algoliaConfig.indexNames[kind];
    
    analyticsViewEvent({
      kind,
      objectIDs,
      userToken: userId,
      index,
    });

    // Move pending items to sent items
    objectIDs.forEach(id => sentItems.current.add(id));
    pendingItems.current.clear();
  }, [kind, userId]);

  const trackVisibleItems = useCallback(() => {
    if (!ref?.current?.hasChildNodes()) return;

    const children = Array.from(ref.current.children);
    const validChildren = children.filter(child => !child.getAttribute("id"));

    validChildren.forEach((child, i) => {
      const item = items[i];
      if (!item) return;
      
      const objectID = item.objectID.toString();
      const isVisible = isInViewport(child);
      const isAlreadySent = sentItems.current.has(objectID);
      const isPending = pendingItems.current.has(objectID);

      if (isVisible && !isAlreadySent && !isPending) {
        pendingItems.current.add(objectID);
      }
    });

    // Clear any pending batch timeout
    if (batchTimeout.current) {
      clearTimeout(batchTimeout.current);
    }

    // Schedule batch send after a short debounce period
    batchTimeout.current = setTimeout(() => {
      sendBatchAnalytics();
    }, 200);
  }, [items, sendBatchAnalytics]);

  useEffect(() => {
    // Reset tracking when items change
    pendingItems.current.clear();
    sentItems.current.clear();
    if (batchTimeout.current) {
      clearTimeout(batchTimeout.current);
      batchTimeout.current = null;
    }
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
      scrollTimeout.current = null;
    }
  }, [items]);

  const handleResize = useCallback(() => {
    if (ref.current) {
      const newBounds = ref.current.getBoundingClientRect();
      setBounds((prevBounds) => {
        if (
          !prevBounds ||
          newBounds.top !== prevBounds.top ||
          newBounds.left !== prevBounds.left ||
          newBounds.width !== prevBounds.width ||
          newBounds.height !== prevBounds.height
        ) {
          trackVisibleItems();
          return newBounds;
        }
        return prevBounds;
      });
    }
  }, [trackVisibleItems]);

  const handleScroll = useCallback(() => {
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }
    scrollTimeout.current = setTimeout(() => {
      handleResize();
    }, 200);
  }, [handleResize]);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!ref.current || !isMounted) return;

    const checkInitialVisibility = () => {
      setTimeout(() => {
        trackVisibleItems();
        // Force send any pending items after initial check
        if (pendingItems.current.size > 0) {
          sendBatchAnalytics();
        }
      }, 300);
    };

    const resizeObserver = new ResizeObserver(handleResize);
    const intersectionObserver = new IntersectionObserver(handleResize, {
      threshold: 0.1,
    });

    resizeObserver.observe(ref.current);
    intersectionObserver.observe(ref.current);
    window.addEventListener("scroll", handleScroll, { passive: true });

    checkInitialVisibility();

    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
    };
  }, [items, isMounted, handleResize, handleScroll, trackVisibleItems, sendBatchAnalytics]);

  // @ts-expect-error capture external events from slider
  ref.onScrollCapture = function () {
    handleScroll();
  };

  return ref;
};